import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import { axiosInstance } from "../../Config";
import { toast } from "react-toastify";

const SendNotification = () => {
    const [users, setUsers] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [dateFilter, setDateFilter] = useState("all");

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const limit = 10;

    const [title, setTitle] = useState("");
    const [message, setMessage] = useState("");

    const [errors, setErrors] = useState({
        title: "",
        message: "",
    });

    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);

    const dateFilters = [
        { value: "all", label: "All Data" },
        { value: "7", label: "Last 7 Days" },
        { value: "30", label: "This Month" },
        { value: "180", label: "Last 6 Months" },
        { value: "365", label: "Last Year" },
    ];

    const getCurrentFilterLabel = () => {
        const filter = dateFilters.find((f) => f.value === dateFilter);
        return filter ? filter.label : "All Data";
    };

 
    const validateField = (name, value) => {
        const v = (value || "").trim();

        if (name === "title") {
            if (!v) return "Title is required.";
            if (v.length < 3) return "Title must be at least 3 characters.";
            if (v.length > 80) return "Title must be at most 80 characters.";
            return "";
        }

        if (name === "message") {
            if (!v) return "Message is required.";
            if (v.length < 5) return "Message must be at least 5 characters.";
            if (v.length > 500) return "Message must be at most 500 characters.";
            return "";
        }

        return "";
    };

    const validateAll = () => {
        const nextErrors = {
            title: validateField("title", title),
            message: validateField("message", message),
        };
        setErrors(nextErrors);
        return Object.values(nextErrors).every((e) => !e);
    };

    useEffect(() => {
        const t = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm.trim());
        }, 500);

        return () => clearTimeout(t);
    }, [searchTerm]);


    const fetchUsers = async (page, search = "", filter = "all") => {
        try {
            setLoading(true);
            const response = await axiosInstance.get(
                `/getAllUsers?page=${page}&limit=${limit}&search=${encodeURIComponent(
                    search
                )}&dateFilter=${filter}`
            );

            if (response.data.success) {
                setUsers(response?.data?.body?.data || []);
                setTotalPages(response?.data?.body?.totalPages || 1);
            } else {
                setUsers([]);
                setTotalPages(1);
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to fetch users");
            setUsers([]);
            setTotalPages(1);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers(currentPage, searchTerm, dateFilter);
    }, [currentPage, debouncedSearchTerm, dateFilter]);

    const isAllCheckedOnPage = useMemo(() => {
        if (!users.length) return false;
        return users.every((u) => selectedIds.includes(u.id));
    }, [users, selectedIds]);

    const toggleSelectOne = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const toggleSelectAllOnPage = () => {
        const pageIds = users.map((u) => u.id);

        setSelectedIds((prev) => {
            const allSelected = pageIds.every((id) => prev.includes(id));
            if (allSelected) return prev.filter((id) => !pageIds.includes(id));
            return Array.from(new Set([...prev, ...pageIds]));
        });
    };

    const clearSelection = () => setSelectedIds([]);


    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
    };

    const handleDateFilter = (value) => {
        setDateFilter(value);
        setCurrentPage(1);
    };

    const onTitleChange = (e) => {
        const val = e.target.value;
        setTitle(val);
        setErrors((prev) => ({ ...prev, title: validateField("title", val) }));
    };

    const onMessageChange = (e) => {
        const val = e.target.value;
        setMessage(val);
        setErrors((prev) => ({ ...prev, message: validateField("message", val) }));
    };

    const canSendAll = useMemo(() => {
        return (
            title.trim().length > 0 &&
            message.trim().length > 0 &&
            !errors.title &&
            !errors.message
        );
    }, [title, message, errors]);

    const canSendSelected = useMemo(() => {
        return canSendAll && selectedIds.length > 0;
    }, [canSendAll, selectedIds.length]);

    const sendToSelected = async () => {
        if (!validateAll()) return;
        if (!selectedIds.length) return toast.error("Please select at least 1 user");

        try {
            setSending(true);

            const payload = {
                user_ids: selectedIds,
                title: title.trim(),
                message: message.trim(),
                sendToAll: 0,
            };

            const res = await axiosInstance.post("/sendNotification", payload);

            if (res.data.success) {
                toast.success("Notification sent");
                setTitle("");
                setMessage("");
                setErrors({ title: "", message: "" });
                clearSelection();
            } else {
                toast.error(res?.data?.message || "Failed to send notification");
            }
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to send notification");
        } finally {
            setSending(false);
        }
    };

    const sendToAll = async () => {
        if (!validateAll()) return;

        try {
            setSending(true);

            const payload = {
                user_ids: [],
                title: title.trim(),
                message: message.trim(),
                sendToAll: 1,
            };

            const res = await axiosInstance.post("/sendNotification", payload);

            if (res.data.success) {
                toast.success("Notification sent to all users");
                setTitle("");
                setMessage("");
                setErrors({ title: "", message: "" });
                clearSelection();
            } else {
                toast.error(res.data.message || "Failed to send notification");
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to send notification");
        } finally {
            setSending(false);
        }
    };

    return (
        <div id="layout-wrapper">
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        <div className="title-box mb-3 pb-1">
                            <h4 className="mb-0 page-title">Send Notification</h4>
                            <nav aria-label="breadcrumb" className="mt-1">
                                <ol className="breadcrumb mb-0">
                                    <li className="breadcrumb-item">
                                        <Link to="/dashboard" className="new">
                                            <i className="ri-home-4-fill me-1 new" /> Home
                                        </Link>
                                    </li>
                                    <li className="breadcrumb-item active" aria-current="page">
                                        Send Notification
                                    </li>
                                </ol>
                            </nav>
                        </div>

                        {/* Composer */}
                        <div className="card mb-3">
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label className="mb-1 fw-medium">Title *</label>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.title ? "is-invalid" : ""}`}
                                            placeholder="Enter title"
                                            value={title}
                                            onChange={onTitleChange}
                                            disabled={sending}
                                        />
                                        {errors.title && <div className="invalid-feedback">{errors.title}</div>}
                                    </div>

                                    <div className="col-md-6 mb-3">
                                        <label className="mb-1 fw-medium">
                                            Selected Users: <b>{selectedIds.length}</b>
                                        </label>
                                        <div className="d-flex gap-2">
                                            <button
                                                type="button"
                                                className="btn btn-danger"
                                                onClick={sendToAll}
                                                disabled={!canSendAll || sending}
                                            >
                                                {sending ? "Sending..." : "Send to All"}
                                            </button>

                                            <button
                                                type="button"
                                                className="btn btn-success"
                                                onClick={sendToSelected}
                                                disabled={!canSendSelected || sending}
                                            >
                                                {sending ? "Sending..." : "Send to Selected"}
                                            </button>

                                            <button
                                                type="button"
                                                className="btn btn-secondary"
                                                onClick={clearSelection}
                                                disabled={sending || selectedIds.length === 0}
                                            >
                                                Clear Selection
                                            </button>
                                        </div>
                                    </div>

                                    <div className="col-12">
                                        <label className="mb-1 fw-medium">Message *</label>
                                        <textarea
                                            className={`form-control ${errors.message ? "is-invalid" : ""}`}
                                            rows={4}
                                            placeholder="Enter notification message..."
                                            value={message}
                                            onChange={onMessageChange}
                                            disabled={sending}
                                        />
                                        {errors.message && (
                                            <div className="invalid-feedback d-block">{errors.message}</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card">
                            <div className="card-body cusbar">
                                {loading ? (
                                    <div className="loader" style={{ position: 'absolute', top: '50%', left: '59%', transform: 'translate(-50%, -50%)' }}>
                                        <div className="spinner-border" role="status" style={{ width: '50px', height: '50px', color: "#963F02" }}>
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="card-head mb-3 d-flex justify-content-between align-items-center">
                                            <div className="d-flex">
                                                <div className="tbl-search position-relative me-2">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Search.."
                                                        value={searchTerm}
                                                        onChange={handleSearchChange}
                                                    />
                                                    <i className="ri-search-line" />
                                                </div>

                                                <div className="dropdown tbl-drop">
                                                    <button
                                                        className="btn btn-light dropdown-toggle"
                                                        type="button"
                                                        data-bs-toggle="dropdown"
                                                        aria-expanded="false"
                                                    >
                                                        {getCurrentFilterLabel()}
                                                    </button>
                                                    <ul className="dropdown-menu">
                                                        {dateFilters.map((filter) => (
                                                            <li key={filter.value}>
                                                                <button
                                                                    className="dropdown-item"
                                                                    type="button"
                                                                    onClick={() => handleDateFilter(filter.value)}
                                                                >
                                                                    {filter.label}
                                                                </button>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>

                                            {/* <button
                                                type="button"
                                                className="btn btn-outline-dark"
                                                onClick={toggleSelectAllOnPage}
                                                disabled={users.length === 0}
                                                title="Select/Deselect all users on this page"
                                            >
                                                {isAllCheckedOnPage ? "Unselect Page" : "Select Page"}
                                            </button> */}
                                        </div>

                                        <div className="table-responsive table-card border-top">
                                            <div data-simplebar="" className="cus-scroll scmob">
                                                <table className="table table-centered cus-nowrap align-middle hltr mb-0">
                                                    <thead>
                                                        <tr>
                                                            <th style={{ width: 80 }}>
                                                                <div className="form-check">
                                                                    <input
                                                                        className="form-check-input"
                                                                        type="checkbox"
                                                                        checked={isAllCheckedOnPage}
                                                                        onChange={toggleSelectAllOnPage}
                                                                        disabled={users.length === 0}
                                                                    />
                                                                </div>
                                                            </th>
                                                            <th>Name</th>
                                                            <th>Email</th>
                                                        </tr>
                                                    </thead>

                                                    <tbody>
                                                        {users.length > 0 ? (
                                                            users.map((u) => (
                                                                <tr key={u?.id}>
                                                                    <td>
                                                                        <div className="form-check">
                                                                            <input
                                                                                className="form-check-input"
                                                                                type="checkbox"
                                                                                checked={selectedIds.includes(u?.id)}
                                                                                onChange={() => toggleSelectOne(u?.id)}
                                                                                disabled={sending}
                                                                            />
                                                                        </div>
                                                                    </td>
                                                                    <td>{u?.name || "-"}</td>
                                                                    <td>{u?.email || "-"}</td>
                                                                </tr>
                                                            ))
                                                        ) : (
                                                            !loading && <tr>
                                                                <td colSpan="3" style={{ textAlign: "center", padding: "20px" }}>
                                                                    No users found
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                                {totalPages && totalPages > 1 ?
                                                    <div className="d-flex justify-content-center align-items-center mt-3">
                                                        <Stack spacing={2} className="d-flex justify-content-center mt-3">
                                                            <Pagination
                                                                count={totalPages}
                                                                page={currentPage}
                                                                onChange={handlePageChange}
                                                                color="error"
                                                            />
                                                        </Stack>
                                                    </div>
                                                    : null}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default SendNotification;